'use client';

import { Fragment } from 'react';
import { useFormikContext } from 'formik';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import RadioGroupField from '../forms/radio-group-field';
import TextField from '../forms/text-field';
import MaterialLink from '../material-link';
import { eventSeriesPath } from '../../utils/slugify';
import FormGroup from './form-group';
import FormHost from './form-host';
import FormCategories from './form-categories';
import FormAgeRestriction from './form-age-restriction';
import FormWeatherCancellations from './form-weather-cancellations';
import type { Event } from '../../types/Event';
import type { EventFormValues } from './form';

type Props = {
  disabled: boolean;
  forceTouched: boolean;
  event?: Event;
  isEventSeries?: boolean;
};

const visibilityOptions = [
  {
    label: 'Public — appears on city calendar',
    value: 'PUBLIC',
  },
  {
    label: 'Group members only',
    value: 'GROUP',
  },
  {
    label: 'Unlisted — anyone with the link',
    value: 'UNLISTED',
  },
  {
    label: 'Private — invite only',
    value: 'PRIVATE',
  },
];

const FormAudience = ({ disabled, forceTouched, event, isEventSeries }: Props) => {
  const { values: { eventType } } = useFormikContext<EventFormValues>();

  return (
    <Fragment>
      <Typography variant="h2">Audience & Visibility</Typography>

      {event?.series && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {'Privacy, group, and host are set by the '}
          <MaterialLink href={eventSeriesPath(event.series.id, event.series.name)}>
            {event.series.name}
          </MaterialLink>
          {' series.'}
        </Typography>
      )}

      <RadioGroupField
        name="visibility"
        label="Visibility"
        disabled={disabled || isEventSeries}
        touched={forceTouched}
        options={visibilityOptions}
      />

      <FormGroup
        disabled={disabled || isEventSeries}
        forceTouched={forceTouched}
      />

      <FormHost
        disabled={disabled || isEventSeries}
        forceTouched={forceTouched}
      />

      <Divider sx={{ my: 3 }} />
      <Typography variant="h3">Event Metadata</Typography>

      <FormCategories
        disabled={disabled}
        forceTouched={forceTouched}
      />

      <FormAgeRestriction
        disabled={disabled}
        touched={forceTouched}
        sx={{ my: 2 }}
      />

      <TextField
        name="audienceRestriction"
        label="Audience restriction (optional)"
        disabled={disabled}
        touched={forceTouched}
        helperText={`Example: "WTF/NB" or "Asian/NH/PI"`}
      />

      {eventType !== 'ONLINE_EVENT' && (
        <FormWeatherCancellations
          disabled={disabled}
          touched={forceTouched}
          sx={{ my: 2 }}
        />
      )}
    </Fragment>
  );
};

export default FormAudience;
